import { useState, useEffect } from 'react';

import useSetState from '#utils/use-set-state';
import { validate } from '#common/validation';

type FormValues = Record<string, unknown>;
type TouchedMap = Record<string, boolean>;
type ValidateResult = ReturnType<typeof validate>;
type SubmitHandler = (values: FormValues, isValid: boolean) => void;

interface ChangeTarget {
  name: string;
  touchedName?: string;
  value?: unknown;
  type?: string;
  checked?: boolean;
}

interface ChangeEventLike {
  target?: ChangeTarget;
}

export default function useForm({
  initialState = {},
  validations = [],
}: {
  initialState?: FormValues | undefined;
  validations?: Parameters<typeof validate>[0] | undefined;
}) {
  const [values, setValues] = useSetState<FormValues>(initialState);
  const [touched, setTouched] = useState<TouchedMap>({});
  const [errors, setErrors] = useState<ValidateResult['errors']>({});
  const [errorMessages, setErrorMessages] = useState<ValidateResult['errorMessages']>({});
  const [isValid, setValid] = useState(false);
  // TODO simple dirty check on values change
  // can be improved to compare with the unsaved values
  const [isDirty, setDirty] = useState(false);

  useEffect(() => {
    // TODO rewrite handleChange
    // only set touched in the handleChange
    // and do validation here
    const validateResult = validate(validations, values, touched, window.subscriptions);
    setValid(validateResult.isValid);
    setErrors(validateResult.errors);
    setErrorMessages(validateResult.errorMessages);
  }, [touched]);

  // the handleChange cannot be called multiple times
  // if we need change multiple value we should use an array
  // for the second argument
  const handleChange = (
    event: ChangeEventLike | null | undefined,
    data?: ChangeTarget | ChangeTarget[],
  ) => {
    let newValues = values;
    let newTouched = touched;
    const getNewValues = (change?: ChangeTarget) => {
      const target = (change && typeof change === 'object'
        ? change
        : event?.target) as ChangeTarget;

      const targetName = target.name;
      const touchedName = target.touchedName || target.name;

      let targetValue: unknown = target.value;
      if (target.type === 'checkbox') {
        targetValue = target.checked;
      } else if (target.type === 'number') {
        targetValue = Number(targetValue);
      }

      newValues = {
        ...newValues,
        [targetName]: targetValue,
      };
      newTouched = {
        ...newTouched,
        [targetName]: true,
        [touchedName]: true,
      };
    };

    const changes = ([] as (ChangeTarget | undefined)[]).concat(data);
    changes.forEach((c) => {
      getNewValues(c);
    });

    const {
      isValid,
      errors,
      errorMessages,
    } = validate(validations, newValues, newTouched, window.subscriptions);
    setValues(newValues);
    setValid(isValid);
    setDirty(true);
    setErrors(errors);
    setErrorMessages(errorMessages);
    setTouched(newTouched);
  };

  let submitHandler: SubmitHandler | null = null;
  const addSubmitListener = (handler: SubmitHandler) => {
    if (submitHandler) {
      return;
    }
    submitHandler = handler;
  };

  const handleSubmit = (event: { preventDefault: () => void }) => {
    event.preventDefault();

    // mark all control touched and do validate
    // "loading" is for UI rendering, exclude it
    const newTouched = Object.keys(initialState)
      .filter((k) => k !== 'loading')
      .reduce<TouchedMap>((a, b) => {
        a[b] = true;
        return a;
      }, {});

    const {
      isValid,
      errors,
      errorMessages,
    } = validate(validations, values, newTouched, window.subscriptions);
    setErrors(errors);
    setErrorMessages(errorMessages);
    setTouched(newTouched);
    setDirty(false);
    submitHandler!(values, isValid);
  };

  const resetForm = (state: FormValues) => {
    setDirty(false);
    setValues(state);
    setTouched({});
  };

  return {
    values,
    setValues,
    handleChange,
    isValid,
    isDirty,
    setDirty,
    errors,
    errorMessages,
    touched,
    setTouched,
    handleSubmit,
    addSubmitListener,
    resetForm,
  };
}
